import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { PAYMENT_STATUS } from 'arbitrip-common/general/constants';

import ReservationActions from '@/actions/ReservationActions';

import ReservationDetails from './ReservationDetails';
import Travelers from './Travelers';
import TermsOfSupplier from './TermsOfSupplier';

import { ReactComponent as CheckmarkIcon } from '@/img/confirmation_page/checkmark.svg';
import isracardBanner from '@/img/confirmation_page/isracard-banner.png';
import { BnplIllustration } from '../reservation-payment/components/BnplTopBanner.react';
import ExpediaDealDetailsContainer from '../reservation-payment/components/ExpediaDealDetailsContainer.react';
import PriceSummary from '../reservation-payment/components/PriceSummary.react';
import { isIsracardCompany } from '@/utils/isCompany';
import { computeBnplAmounts } from '@/utils/PayLater';
import { getIsracardBaseUrl } from 'arbitrip-common/client/utils/IsracardUrls';
import { addUtmDataToUrl } from 'arbitrip-common/client/utils/UtmUtils';

const ConfirmationPage = ({ reservation, hotel, profile, couponValidation, points }) => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const isIsracard = isIsracardCompany();
	const isracardBaseUrl = getIsracardBaseUrl();

	const { id, short_id: shortId, travelers, deal } = reservation;
	const nonRefundable = _.get(reservation, 'deal.dca.nonRefundable');
	const multiLanguageTexts = _.get(reservation, 'deal.original.dca.cancellationPoliciesMultiLanguageTexts');
	const defaultText = _.get(reservation, 'deal.original.dca.cancellationPoliciesText');
	const cancellationPoliciesText = (multiLanguageTexts ? multiLanguageTexts[i18n.language] : defaultText)?.split(
		'\n',
	);
	const city = _.get(hotel, 'city');
	const payment = reservation?.payment ?? {};
	const isBnpl = payment.payment_type === 'bnpl';
	const bnplChargeDate = payment.charge_date;
	const userChosenPoints = _.get(points, 'user_chosen_points', 0);
	const exactPointsApplied = _.get(points, 'exact_points_applied', 0);
	const pointsExRate = _.get(points, 'points_ex_rate', 0);
	const arbitripPointsRate = _.get(profile, 'loyalty.arbitrip_points_to_currency_exchange_rate', 0);
	const pointsExchangeRate = arbitripPointsRate || (pointsExRate > 0 ? pointsExRate : 0);
	const pointsDiscountValue =
		exactPointsApplied > 0
			? exactPointsApplied
			: userChosenPoints > 0
			  ? userChosenPoints * pointsExchangeRate
			  : 0;
	// Show the authoritative server-persisted charge amount (post coupon + points, fee-included) — the Poller
	// updates payment.bnpl.usd_amount_with_fee with this value once the booking is saved. The client-side recompute
	// (BNPL base − points − coupon, fee-adjusted) is only a fallback for bookings missing the persisted value.
	const couponDiscountValue =
		couponValidation?.eligible && _.isFinite(couponValidation?.discount?.amount)
			? Math.max(0, couponValidation.discount.amount || 0)
			: 0;
	const bnplAmounts = computeBnplAmounts({
		bnplIlsAmount: reservation.bnpl_ils_amount,
		bnplIlsRate: reservation.bnpl_ils_rate,
		bnplFeePercentage: reservation.bnpl_fee_percentage ?? 2,
		pointsDiscountIls: (pointsDiscountValue || 0) + couponDiscountValue,
	});
	const bnplAmount = _.isFinite(payment.bnpl?.usd_amount_with_fee)
		? payment.bnpl.usd_amount_with_fee
		: bnplAmounts && bnplAmounts.payLaterUsd != null
			? bnplAmounts.payLaterUsd
			: undefined;
	const isBnplCharged = payment.status === PAYMENT_STATUS.BILLED;
	const isBnplCancelled = payment.status === PAYMENT_STATUS.CANCELLED;
	const isBnplChargeSoon = isBnpl && bnplChargeDate && !isBnplCharged && !isBnplCancelled &&
		new Date(bnplChargeDate) < new Date(Date.now() + 48 * 60 * 60 * 1000);
	const cancellationDeadline = _.get(reservation, 'deal.dca.dealIsFullyRefundableTill');
	const chargeDateFormatted = bnplChargeDate
		? new Date(bnplChargeDate).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
		: '';
	const cancellationDateFormatted = cancellationDeadline
		? new Date(cancellationDeadline).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
		: '';

	useEffect(() => {
		window.scrollTo(0, 0);

		return () => {
			ReservationActions.clearReservation();
		};
	}, []);

	// var PHONE = t('SupportPhoneNumber');
	const isracardBlocks = useMemo(()=>[
		{
			condition: isIsracard,
			className: "isracard-blocks__block--lets-close-deal-banner",
			titleKey: "AndNowLetsCloseAflight",
			descKey: "TheVacationYouDreamedAboutWaitsForYou",
			ctaKey: "ToFlights",
			url : addUtmDataToUrl(t('FlightsUrl'), isIsracard)
		},
		{
			condition: isIsracard,
			className: "isracard-blocks__block--flight-abroad-banner",
			titleKey: "TravelAbroadIsracardTitle",
			descKey: "TravelAbroadIsracardBenefits",
			ctaKey: "TravelAbroadIsracardCta",
			url : addUtmDataToUrl(t('TravelAbroadIsracardCtaUrl'), isIsracard)
		},
		],[isIsracard]);

	return (
		<div className="confirmation-page">
			<div className="order-confirmation">
				<div className="order-confirmation__title">{t('OrderConfirmation')}</div>
				{shortId && city && (
					<div className="order-confirmation__content">
						<CheckmarkIcon />
						<div className="order-confirmation__text">
							{t('ReservationProcessing', { id: shortId, city: city })}
						</div>
					</div>
				)}
			</div>
			<div className="confirmation-page__content">
				<div className="confirmation-page__main">
					<Travelers travelers={travelers} bookingId={id} isIsracard={isIsracard} isracardBaseUrl={isracardBaseUrl} />

					{isBnpl && (
						<div className="bnpl-confirmation-banner">
							<div className="bnpl-confirmation-banner__content">
								{isBnplChargeSoon && (
									<div className="bnpl-confirmation-banner__alert">
										{t('BnplChargeDateAlert')}
									</div>
								)}
								{chargeDateFormatted && (
									<div className="bnpl-confirmation-banner__title">
										{t('BnplChargedOn', {
											amount: typeof bnplAmount === 'number' ? bnplAmount.toFixed(0) : '',
											date: chargeDateFormatted,
										})}
									</div>
								)}
								<div className="bnpl-confirmation-banner__subtitle">
									{t('BnplEmailBeforeCharge')}
								</div>
							</div>
							<div className="bnpl-confirmation-banner__illustration">
								<BnplIllustration />
							</div>
						</div>
					)}

					<div className="cancellation-terms">
						<div className="cancellation-terms__content">
							<div className="title">{t('CancellationTerms')}</div>
							<div className="cancellation-terms__text">
								{cancellationPoliciesText.map((line, index) => (
									<div
										key={index}
										className={`cancellation-terms__${
											index === 0 ? (nonRefundable ? 'text-red' : 'text-green') : 'term'
										}`}
									>
										{line.trim()}
									</div>
								))}
							</div>
						</div>
						<div>
							<button
								className="confirmation-page__button"
								data-testid="confirmation-view-booking-button"
								onClick={() => isIsracard ? window.open(addUtmDataToUrl(`${isracardBaseUrl}/account-orders`, isIsracard), '_blank', 'noopener,noreferrer') : navigate('/bookings', { state: { id: id }, replace: true })}
							>
								{t('ViewYourBooking')}
							</button>
						</div>
					</div>

					<TermsOfSupplier deal={deal} />

				{isracardBlocks.filter(block => block.condition).map((block, index) => (
					<div key={`isracard_block_${index}`} className={`isracard-blocks__block ${block.className}`}>
					<div className="isracard-blocks__flight-content">
						<div className="isracard-blocks__text">
						<div className="isracard-blocks__title">
							{t(block.titleKey)}
						</div>
						<div className="isracard-blocks__desc">
							{t(block.descKey)}
						</div>
						</div>
					</div>

					<button
						className="isracard-blocks__find-flights"
						onClick={() =>window.open(block.url, "_blank", "noopener,noreferrer")}
					>
						{t(block.ctaKey)}
					</button>
					</div>
				))}

				<div className="contact-us">
						<div className="contact-us__content">
							<div className="title">{t('ContactUs')}</div>
							<div className="contact-us__text">
								{t('ContactUsMessage')}
							</div>
						</div>
						<button
							className="confirmation-page__button"
							onClick={() => isIsracard ? window.open(addUtmDataToUrl(`${isracardBaseUrl}/contact-us`, isIsracard), '_blank', 'noopener,noreferrer') : navigate('/contact-us')}
						>
							{t('ContactUsCTA')}
						</button>
					</div>
				</div>

			<div className="confirmation-page__side">
				<ReservationDetails hotel={hotel} reservation={reservation} />
				{reservation.deal?.expedia_pricing ? (
					<ExpediaDealDetailsContainer profile={profile} reservation={reservation} isConfirmation={true} />
				) : (
					<PriceSummary
						reservation={reservation}
						profile={profile}
						hotel={hotel}
						couponValidation={couponValidation}
						points={points}
						hideTermsOfSupplierDisclaimer={true}
						isConfirmation={true}
						selectedPaymentMethod={payment.payment_type}
					/>
				)}
			</div>
			</div>
		</div>
	);
};

export default ConfirmationPage;
